import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, interval } from 'rxjs';
import { map } from 'rxjs/operators';
import _ from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ClauseLibraryService, IClause, IClauseBundle, CLAUSE_SOURCE, CLAUSE_STATUS, CLAUSE_CONFIDENCE, ClauseSortBy, ClauseSortOrder } from '../../../../../../services/clause-library.service';
import { ToastService } from '../../../../../../../@core/services/toast.service';
import { UserService } from '../../../../../../services/user.service';
import { DataTableComponent } from '../../../../../../../@shared/components/data-table/data-table.component';
import { SearchBarComponent } from '../../../../../../../@shared/components/search-bar/search-bar.component';
import { TableColumn, TableConfig } from '../../../../../../../@shared/models/table.models';
import { REVIEW_CLAUSES_TABLE_COLUMNS, REVIEW_CLAUSES_TABLE_CONFIG } from './review-clauses-table.config';
import { ButtonComponent } from '../../../../../../../@core/components/ui/button/button.component';
import { PopoverComponent } from '../../../../../../../@core/components/ui/popover/popover.component';
import { MultiSelectComponent } from '../../../../../../../@core/components/ui/multi-select';
import { SelectComponent, SelectOption, SelectConfig } from '../../../../../../../@core/components/ui/select';
import { DatepickerComponent } from '../../../../../../../@core/components/ui/datepicker';
import { UserAvatarPipe } from '../../../../../../../@shared/pipes/user-avatar-pipe';
import { TextCasePipe } from '../../../../../../../@shared/pipes/textCase.pipe';
import { ClauseBundleService } from 'src/app/contract-management/services/clause-bundle.service';
import { IconSourceEnum } from '../../../../../../../@core/components/ui/icon/icon.component';
import { CLAUSE_LIBRARY } from 'src/app/common/svgs';
import { BlobLoaderComponent } from 'src/app/@shared/components/blob-loader/blob-loader.component';
import saveAs from 'file-saver';
import { MenuComponent, CustomMenuItem } from '../../../../../../../@core/components/ui/menu/menu.component';
import { DeleteConfirmationComponent } from '../../../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';

const ALL_BUNDLES_VALUE = '';

@Component({
    selector: 'app-review-clauses',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogComponent,
        DrawerModule,
        TooltipModule,
        ProgressSpinnerModule,
        ConfirmDialogModule,
        SearchBarComponent,
        DataTableComponent,
        ButtonComponent,
        PopoverComponent,
        MultiSelectComponent,
        SelectComponent,
        DatepickerComponent,
        UserAvatarPipe,
        TextCasePipe,
        BlobLoaderComponent,
        MenuComponent,
        DeleteConfirmationComponent,
    ],
    providers: [ConfirmationService],
    templateUrl: './review-clauses.component.html',
    styleUrl: './review-clauses.component.scss'
})
export class ReviewClausesComponent implements OnInit {
    @ViewChild('clauseBundleTemplateRef') clauseBundleTemplateRef!: TemplateRef<any>;
    @ViewChild('sourceTemplateRef') sourceTemplateRef!: TemplateRef<any>;
    @ViewChild('createdByTemplateRef') createdByTemplateRef!: TemplateRef<any>;
    @ViewChild('confidenceTemplateRef') confidenceTemplateRef!: TemplateRef<any>;
    @ViewChild('createdAtTemplateRef') createdAtTemplateRef!: TemplateRef<any>;
    @ViewChild('approveOrRejectTemplateRef') approveOrRejectTemplateRef!: TemplateRef<any>;
    @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;
    @ViewChild('dataTable') dataTable!: DataTableComponent;
    @ViewChild('sortFilterPopover') sortFilterPopover!: any;

    searchValue = '';
    clausesLoading = false;
    clausesData: IClause[] = [];
    skip = 0;
    limit = 20;
    totalRecords = 0;

    selectedClauses: IClause[] = [];
    selectedClauseIds: string[] = [];

    selectedBundleId: string = ALL_BUNDLES_VALUE;
    clauseBundleOptions: SelectOption[] = [{ label: 'All clause bundles', value: ALL_BUNDLES_VALUE }];
    bundleSelectConfig: SelectConfig = {
        placeholder: 'Select Clause Bundle',
        searchable: true,
        clearable: false,
        styleClass: 'font-semibold rounded-xl text-black border border-gray-200',
        appendTo: 'body'
    };

    clauseTableColumns: TableColumn[] = [...REVIEW_CLAUSES_TABLE_COLUMNS];
    clauseTableConfig: TableConfig = { ...REVIEW_CLAUSES_TABLE_CONFIG };

    filterDialog = false;
    sourceOptions: { name: string; value: CLAUSE_SOURCE }[] = [
        { name: 'Manual entry', value: CLAUSE_SOURCE.MANUAL },
        { name: 'Excel', value: CLAUSE_SOURCE.EXCEL },
        { name: 'Extracted', value: CLAUSE_SOURCE.EXTRACTED }
    ];
    statusOptions: { name: string; value: CLAUSE_STATUS }[] = [
        { name: 'Approved', value: CLAUSE_STATUS.APPROVED },
        { name: 'Rejected', value: CLAUSE_STATUS.REJECTED },
        { name: 'Pending', value: CLAUSE_STATUS.PENDING }
    ];
    selectedSources: CLAUSE_SOURCE[] = [];
    selectedStatuses: CLAUSE_STATUS[] = [];
    selectedCreators: string[] = [];
    selectedClauseBundles: string[] = [];
    selectedDateRange: Date[] | null = null;
    selectedDateChip: 'today' | 'last7days' | 'last30days' | null = null;
    minUsageCount: number | null = null;
    maxUsageCount: number | null = null;
    userOptions: { label: string; value: string }[] = [];
    clauseBundles: IClauseBundle[] = [];

    showAddEditDialog = false;
    isEditMode = false;
    currentClause: Partial<IClause> & { prompt?: string } = {};
    currentClauseBundleIds: string[] = [];
    saveClauseLoading = false;
    isClauseTextExpanded = false;
    generateClauseTextLoading = false;
    rephraseClauseLoading = false;
    clauseGenerationId = 0;
    dots$ = interval(800).pipe(map((i: number) => '.'.repeat(i % 4)));
    ICON_SOURCE = IconSourceEnum;
    CLAUSE_LIBRARY_ICONS = CLAUSE_LIBRARY;

    showViewDialog = false;
    viewClause: IClause | null = null;

    showDeleteConfirmDialog = false;
    itemsToDeleteCount = 0;

    // Bulk edit dialog (same as clause-library)
    showBulkEditDialog = false;
    editingClauses: IClause[] = [];
    originalClauses: IClause[] = [];
    currentEditIndex = 0;
    savedClauses: Set<number> = new Set();
    totalEditingCount = 0;
    isBulkEditPreviousDisabled = true;
    isBulkEditNextDisabled = true;
    isBulkEditSaveDisabled = true;
    isBulkEditSaving = false;
    bulkEditGenerateLoading = false;
    bulkEditRephraseLoading = false;
    bulkEditClauseGenerationId = 0;
    showPromptForBulkEditDialog = false;
    promptForBulkEditText = '';
    bulkEditClauseTextExpanded = false;

    showBulkApproveConfirmDialog = false;
    showBulkRejectConfirmDialog = false;

    showBulkLinkBundleDialog = false;
    bulkLinkSearchValue = '';
    selectedBundleIdsForBulkLink: string[] = [];
    bulkLinkingToBundles = false;

    CLAUSE_SOURCE = CLAUSE_SOURCE;
    CLAUSE_CONFIDENCE = CLAUSE_CONFIDENCE;
    ClauseSortBy = ClauseSortBy;

    clauseSortBy: ClauseSortBy = ClauseSortBy.CreatedAt;
    clauseSortOrder: ClauseSortOrder = -1;

    constructor(
        private clauseLibraryService: ClauseLibraryService,
        private toast: ToastService,
        private userService: UserService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private clauseBundleService: ClauseBundleService
    ) { }

    ngOnInit() {
        this.getClauseBundles();
        this.getUsers();
        this.route.queryParamMap.subscribe(params => {
            const bundleId = params.get('bundleId') ?? undefined;
            const optionExists = this.clauseBundleOptions.some(opt => opt.value === bundleId);
            this.selectedBundleId = bundleId && optionExists ? bundleId : ALL_BUNDLES_VALUE;
            this.skip = 0;
            this.getClauses();
        });
    }

    get editClauseBundleOptions(): SelectOption[] {
        return this.clauseBundleOptions.filter(o => o.value !== ALL_BUNDLES_VALUE);
    }

    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            clauseBundleTemplate: this.clauseBundleTemplateRef,
            sourceTemplate: this.sourceTemplateRef,
            createdByTemplate: this.createdByTemplateRef,
            confidenceTemplate: this.confidenceTemplateRef,
            createdAtTemplate: this.createdAtTemplateRef,
            approveOrRejectTemplate: this.approveOrRejectTemplateRef,
            actionsTemplate: this.actionsTemplateRef
        };
    }

    getClauseBundles() {
        this.clauseBundleService.getBundles({}, false).subscribe((res: any) => {
            if (res && res.success) {
                this.clauseBundleOptions = [...this.clauseBundleOptions, ...(res.data.bundles || []).map((bundle: IClauseBundle) => ({
                    label: bundle.name,
                    value: bundle._id
                }))];
                const bundleId = this.route.snapshot.queryParamMap.get('bundleId');
                if (bundleId && this.clauseBundleOptions.some(o => o.value === bundleId)) {
                    this.selectedBundleId = bundleId;
                } else {
                    this.selectedBundleId = this.clauseBundleOptions[0]?.value ?? ALL_BUNDLES_VALUE;
                }
                this.getClauses();
            }
        });
    }

    getUsers() {
        this.userService.getWorkspaceUsers({ includeAdmins: true }).subscribe(res => {
            if (res?.success && res?.data) {
                this.userOptions = res.data.map(user => ({
                    label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    value: user._id
                }));
            }
        });
    }

    getClauses(append = false) {
        this.clausesLoading = true;
        this.clauseTableConfig = { ...this.clauseTableConfig, loading: true };

        const bundleFilter = this.selectedBundleId === ALL_BUNDLES_VALUE
            ? (this.selectedClauseBundles.length > 0 ? this.selectedClauseBundles : undefined)
            : [this.selectedBundleId];

        const filters = {
            searchValue: this.searchValue,
            skip: this.skip,
            limit: this.limit,
            status: CLAUSE_STATUS.PENDING,
            source: this.selectedSources.length > 0 ? this.selectedSources : undefined,
            createdBy: this.selectedCreators.length > 0 ? this.selectedCreators : undefined,
            clauseBundle: bundleFilter,
            createdAt: this.selectedDateRange && this.selectedDateRange.length === 2
                ? { startDate: this.selectedDateRange[0], endDate: this.selectedDateRange[1] }
                : undefined,
            minUsageCount: this.minUsageCount ?? undefined,
            maxUsageCount: this.maxUsageCount ?? undefined,
            sortBy: this.clauseSortBy,
            sortOrder: this.clauseSortOrder
        };

        this.clauseLibraryService.getClauses(filters).subscribe(res => {
            this.clausesLoading = false;
            this.clauseTableConfig = { ...this.clauseTableConfig, loading: false };
            if (res.success) {
                if (append) {
                    this.clausesData = [...this.clausesData, ...res.data.clauses];
                } else {
                    this.clausesData = res.data.clauses;
                }
                this.totalRecords = res.data.total;
            }
        });
    }

    onBundleChange(bundleId: string) {
        this.selectedBundleId = bundleId;
        this.selectedClauseIds = [];
        this.selectedClauses = [];
        this.dataTable?.clearSelection();
        this.skip = 0;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: bundleId === ALL_BUNDLES_VALUE ? {} : { bundleId },
            queryParamsHandling: 'merge'
        });
        this.getClauses();
    }

    onSelectionChange(event: any) {
        this.selectedClauses = event;
        this.selectedClauseIds = event.map((item: IClause) => item._id);
    }

    clearSelections() {
        this.selectedClauses = [];
        this.selectedClauseIds = [];
        this.dataTable?.clearSelection();
    }

    onPageChange(event: any) {
        this.skip = event.first;
        this.limit = event.rows;
        this.getClauses();
    }

    searchClauses = _.debounce(() => {
        this.skip = 0;
        this.clearSelections();
        this.getClauses();
    }, 300);

    onSearch() {
        this.searchClauses();
    }

    setClauseSort(sortBy: ClauseSortBy, sortOrder?: ClauseSortOrder) {
        this.clauseSortBy = sortBy;
        this.clauseSortOrder = sortOrder ?? (sortBy === ClauseSortBy.Name ? 1 : -1);
        this.clausesData = [];
        this.skip = 0;
        this.sortFilterPopover?.hide();
        this.getClauses();
    }

    setSortOrder(order: ClauseSortOrder) {
        this.clauseSortOrder = order;
        this.clausesData = [];
        this.skip = 0;
        this.sortFilterPopover?.hide();
        this.getClauses();
    }

    openFilterDialog() {
        this.filterDialog = true;
    }

    applyFilter() {
        this.filterDialog = false;
        this.skip = 0;
        this.clearSelections();
        this.getClauses();
    }

    clearFilter() {
        this.selectedSources = [];
        this.selectedStatuses = [];
        this.selectedCreators = [];
        this.selectedClauseBundles = [];
        this.selectedDateRange = null;
        this.selectedDateChip = null;
        this.minUsageCount = null;
        this.maxUsageCount = null;
        this.filterDialog = false;
        this.skip = 0;
        this.clearSelections();
        this.getClauses();
    }

    selectDateRange(range: 'today' | 'last7days' | 'last30days') {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        let startDate = new Date();
        switch (range) {
            case 'today':
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'today';
                break;
            case 'last7days':
                startDate.setDate(today.getDate() - 7);
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'last7days';
                break;
            case 'last30days':
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'last30days';
                break;
        }
        this.selectedDateRange = [startDate, today];
    }

    getDateChipClass(range: 'today' | 'last7days' | 'last30days'): string {
        return this.selectedDateChip === range
            ? 'bg-gray-700 text-white hover:bg-gray-800'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }

    onDateRangeChange() {
        this.selectedDateChip = null;
    }

    getClauseBundleName(clause: IClause): string {
        if (!clause.clauseBundles || clause.clauseBundles.length === 0) return '-';
        const first = clause.clauseBundles[0];
        return typeof first === 'string' ? first : (first.name || '-');
    }

    getCreatedByName(clause: IClause): string {
        if (typeof clause.createdBy === 'string') return clause.createdBy;
        return clause.createdBy?.fullName || clause.createdBy?.email || 'Unknown';
    }

    getSourceLabel(source: CLAUSE_SOURCE): string {
        switch (source) {
            case CLAUSE_SOURCE.MANUAL: return 'Manual entry';
            case CLAUSE_SOURCE.EXCEL: return 'Excel';
            case CLAUSE_SOURCE.EXTRACTED: return 'Extracted';
            default: return source || '-';
        }
    }

    getConfidenceLabel(confidence?: number | CLAUSE_CONFIDENCE): string {
        if (confidence === undefined || confidence === null) return '-';
        if (typeof confidence === 'number') {
            if (confidence >= 0.7) return 'High';
            if (confidence >= 0.4) return 'Medium';
            if (confidence >= 0) return 'Low';
            return '-';
        }
        switch (confidence) {
            case CLAUSE_CONFIDENCE.HIGH: return 'High';
            case CLAUSE_CONFIDENCE.MEDIUM: return 'Medium';
            case CLAUSE_CONFIDENCE.LOW: return 'Low';
            default: return String(confidence);
        }
    }

    formatDate(date: string | Date | undefined): string {
        if (!date) return '-';
        const d = new Date(date);
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: 'long' });
        const year = d.getFullYear();
        const suffix = (n: number) => (n > 3 && n < 21) ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
        return `${day}${suffix(day)} ${month} ${year}`;
    }

    editClause(clause: IClause) {
        this.isEditMode = true;
        this.currentClause = { ...clause, prompt: (clause as any).prompt ?? '' };
        this.currentClauseBundleIds = this.getClauseBundleIds(clause);
        this.showAddEditDialog = true;
    }

    getClauseMenuOptions(clause: IClause): CustomMenuItem[] {
        return [
            { label: 'Edit', icon: 'pi pi-pencil', value: 'edit', callback: () => this.editClause(clause) }
        ];
    }

    /** Handles visibility change from p-dialog. Intercepts close (false) while generating/rephrasing so the dialog never closes before the confirm; avoids close-then-reopen flicker. */
    onAddEditDialogVisibleChange(value: boolean) {
        if (value === false) {
            if (this.generateClauseTextLoading || this.rephraseClauseLoading) {
                this.confirmationService.confirm({
                    message: 'The clause is currently being generated. If you exit now, it will not be created. Are you sure you want to cancel?',
                    header: 'Cancel generation?',
                    acceptLabel: 'Yes',
                    rejectLabel: 'No',
                    accept: () => {
                        this.clauseGenerationId++;
                        this.showAddEditDialog = false;
                        this.generateClauseTextLoading = false;
                        this.rephraseClauseLoading = false;
                        this.isClauseTextExpanded = false;
                        this.currentClause = {};
                    },
                    reject: () => { }
                });
                return;
            }
            this.showAddEditDialog = false;
            this.isClauseTextExpanded = false;
            this.currentClause = {};
        } else {
            this.showAddEditDialog = true;
        }
    }

    getClauseBundleIds(clause: IClause): string[] {
        if (!clause.clauseBundles || clause.clauseBundles.length === 0) return [];
        return clause.clauseBundles.map(b => (typeof b === 'string' ? b : b._id));
    }

    toggleClauseTextExpanded() {
        this.isClauseTextExpanded = !this.isClauseTextExpanded;
    }

    generateClauseTextForEdit() {
        const name = this.currentClause.name?.trim();
        if (!name) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.clauseGenerationId++;
        const requestId = this.clauseGenerationId;
        this.generateClauseTextLoading = true;
        this.clauseLibraryService.generateClauseText(name, '').subscribe({
            next: (res) => {
                this.generateClauseTextLoading = false;
                if (requestId !== this.clauseGenerationId) return;
                if (res?.success && res?.data?.clauseText != null) {
                    this.currentClause = { ...this.currentClause, text: res.data.clauseText };
                    this.toast.success('Clause text generated');
                } else {
                    this.toast.error(res?.message || 'Failed to generate clause text');
                }
            },
            error: () => {
                this.generateClauseTextLoading = false;
                if (requestId !== this.clauseGenerationId) return;
                this.toast.error('Failed to generate clause text');
            }
        });
    }

    rephraseClauseForEdit() {
        const name = this.currentClause.name?.trim() ?? '';
        const text = (this.currentClause.text ?? '').trim();
        if (!text) {
            this.toast.error('Clause text is empty');
            return;
        }
        this.clauseGenerationId++;
        const requestId = this.clauseGenerationId;
        this.rephraseClauseLoading = true;
        this.clauseLibraryService.rephraseClause(name, text).subscribe({
            next: (res) => {
                this.rephraseClauseLoading = false;
                if (requestId !== this.clauseGenerationId) return;
                if (res?.success && res?.data?.clauseText != null) {
                    this.currentClause = { ...this.currentClause, text: res.data.clauseText };
                    this.toast.success('Clause text rephrased');
                } else {
                    this.toast.error(res?.message || 'Failed to rephrase clause text');
                }
            },
            error: () => {
                this.rephraseClauseLoading = false;
                if (requestId !== this.clauseGenerationId) return;
                this.toast.error('Failed to rephrase clause text');
            }
        });
    }

    approveClause(clause: IClause) {
        this.clauseLibraryService.updateClause(clause._id, { status: CLAUSE_STATUS.APPROVED }).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toast.success('Clause approved');
                    this.skip = 0;
                    this.getClauses();
                    this.clearSelections();
                }
            },
            error: () => this.toast.error('Failed to approve clause')
        });
    }

    rejectClause(clause: IClause) {
        this.clauseLibraryService.updateClause(clause._id, { status: CLAUSE_STATUS.REJECTED }).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toast.success('Clause rejected');
                    this.skip = 0;
                    this.getClauses();
                    this.clearSelections();
                }
            },
            error: () => this.toast.error('Failed to reject clause')
        });
    }

    openBulkEdit() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause to edit');
            return;
        }
        if (this.selectedClauseIds.length === 1) {
            const clause = this.selectedClauses[0] ?? this.clausesData.find(c => c._id === this.selectedClauseIds[0]);
            if (clause) this.editClause(clause);
            return;
        }
        this.editingClauses = this.selectedClauses.map(c => ({ ...c }));
        this.originalClauses = this.selectedClauses.map(c => ({ ...c }));
        this.currentEditIndex = 0;
        this.showBulkEditDialog = true;
        this.totalEditingCount = this.editingClauses.length;
        this.isBulkEditPreviousDisabled = true;
        this.isBulkEditNextDisabled = this.editingClauses.length <= 1;
        this.savedClauses.clear();
        this.isBulkEditSaveDisabled = true;
        this.bulkEditGenerateLoading = false;
        this.bulkEditRephraseLoading = false;
        this.bulkEditClauseTextExpanded = false;
    }

    openPromptForBulkEditClauseDialog() {
        const clause = this.editingClauses[this.currentEditIndex];
        if (!clause?.name?.trim()) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.promptForBulkEditText = '';
        this.showPromptForBulkEditDialog = true;
    }

    closePromptForBulkEditClauseDialog() {
        this.showPromptForBulkEditDialog = false;
        this.promptForBulkEditText = '';
    }

    submitPromptForBulkEditClause() {
        const clause = this.editingClauses[this.currentEditIndex];
        const name = (clause?.name ?? '').trim();
        const prompt = (this.promptForBulkEditText ?? '').trim();
        if (!name) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.closePromptForBulkEditClauseDialog();
        this.bulkEditClauseGenerationId++;
        const requestId = this.bulkEditClauseGenerationId;
        const editIndex = this.currentEditIndex;
        this.bulkEditGenerateLoading = true;
        this.clauseLibraryService.generateClauseText(name, prompt).subscribe({
            next: (res: any) => {
                this.bulkEditGenerateLoading = false;
                if (requestId !== this.bulkEditClauseGenerationId) return;
                const clauseText = res?.data?.clauseText ?? (typeof res?.data === 'string' ? res.data : null);
                if (res?.success && clauseText != null && this.editingClauses[editIndex]) {
                    this.editingClauses[editIndex] = { ...this.editingClauses[editIndex], text: clauseText };
                    this.saveClauseInBulkEdit(editIndex);
                    this.toast.success('Clause text generated');
                } else {
                    this.toast.error(res?.message || 'Failed to generate clause text');
                }
            },
            error: () => {
                this.bulkEditGenerateLoading = false;
                if (requestId !== this.bulkEditClauseGenerationId) return;
                this.toast.error('Failed to generate clause text');
            }
        });
    }

    generateClauseTextForBulkEdit() {
        this.openPromptForBulkEditClauseDialog();
    }

    toggleBulkEditClauseTextExpanded() {
        this.bulkEditClauseTextExpanded = !this.bulkEditClauseTextExpanded;
    }

    rephraseClauseForBulkEdit() {
        const clause = this.editingClauses[this.currentEditIndex];
        const name = (clause?.name ?? '').trim();
        const text = (clause?.text ?? '').trim();
        if (!text) {
            this.toast.error('Clause text is empty');
            return;
        }
        this.bulkEditClauseGenerationId++;
        const requestId = this.bulkEditClauseGenerationId;
        const editIndex = this.currentEditIndex;
        this.bulkEditRephraseLoading = true;
        this.clauseLibraryService.rephraseClause(name, text).subscribe({
            next: (res) => {
                this.bulkEditRephraseLoading = false;
                if (requestId !== this.bulkEditClauseGenerationId) return;
                if (res?.success && res?.data?.clauseText != null && this.editingClauses[editIndex]) {
                    this.editingClauses[editIndex] = { ...this.editingClauses[editIndex], text: res.data.clauseText };
                    this.saveClauseInBulkEdit(editIndex);
                    this.toast.success('Clause text rephrased');
                } else {
                    this.toast.error(res?.message || 'Failed to rephrase clause text');
                }
            },
            error: () => {
                this.bulkEditRephraseLoading = false;
                if (requestId !== this.bulkEditClauseGenerationId) return;
                this.toast.error('Failed to rephrase clause text');
            }
        });
    }

    onBulkEditDialogHide() {
        if (this.bulkEditGenerateLoading || this.bulkEditRephraseLoading) {
            this.confirmationService.confirm({
                message: 'The clause is currently being generated. If you exit now, it will not be updated. Are you sure you want to cancel?',
                header: 'Cancel generation?',
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.bulkEditClauseGenerationId++;
                    this.showBulkEditDialog = false;
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    this.onCancelBulkEdit();
                },
                reject: () => {
                    this.showBulkEditDialog = true;
                }
            });
            return;
        }
        this.showBulkEditDialog = false;
        this.onCancelBulkEdit();
    }

    selectClauseForEdit(index: number) {
        if ((this.bulkEditGenerateLoading || this.bulkEditRephraseLoading) && index !== this.currentEditIndex) {
            this.confirmationService.confirm({
                message: 'The clause is currently being generated. If you switch now, the result will not be applied. Are you sure you want to cancel?',
                header: 'Cancel generation?',
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.bulkEditClauseGenerationId++;
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    this.currentEditIndex = index;
                    this.isBulkEditPreviousDisabled = index === 0;
                    this.isBulkEditNextDisabled = index === this.editingClauses.length - 1;
                },
                reject: () => { }
            });
            return;
        }
        this.currentEditIndex = index;
    }

    navigateBulkEditPrevious() {
        if (this.currentEditIndex > 0) this.currentEditIndex--;
        this.isBulkEditPreviousDisabled = this.currentEditIndex === 0;
        this.isBulkEditNextDisabled = this.currentEditIndex === this.editingClauses.length - 1;
    }

    navigateBulkEditNext() {
        if (this.currentEditIndex < this.editingClauses.length - 1) this.currentEditIndex++;
        this.isBulkEditPreviousDisabled = this.currentEditIndex === 0;
        this.isBulkEditNextDisabled = this.currentEditIndex === this.editingClauses.length - 1;
    }

    saveClauseInBulkEdit(index: number) {
        const orig = this.originalClauses[index];
        const edit = this.editingClauses[index];
        const changed = orig.name !== edit.name || orig.text !== edit.text;
        if (changed) this.savedClauses.add(index);
        else this.savedClauses.delete(index);
        this.isBulkEditSaveDisabled = this.savedClauses.size === 0;
    }

    onCancelBulkEdit() {
        this.showBulkEditDialog = false;
        this.editingClauses = [];
        this.originalClauses = [];
        this.currentEditIndex = 0;
        this.resetBulkEditDialog();
        this.isBulkEditPreviousDisabled = true;
        this.bulkEditGenerateLoading = false;
        this.bulkEditRephraseLoading = false;
        this.bulkEditClauseTextExpanded = false;
        this.clearSelections();
    }

    onSaveBulkEdit(checkComplete: boolean = true) {
        const modifiedClauses = Array.from(this.savedClauses).map(index => {
            const clause = this.editingClauses[index];
            return { id: clause._id, name: clause.name ?? '', text: clause.text ?? '' };
        });
        if (checkComplete && modifiedClauses.length !== this.editingClauses.length) {
            this.saveIncompleteClauses();
            return;
        }
        if (modifiedClauses.length === 0) {
            this.toast.error('No changes to save');
            return;
        }
        this.isBulkEditSaving = true;
        const updates = modifiedClauses.map(({ id, name, text }) =>
            this.clauseLibraryService.updateClause(id, { name, text })
        );
        forkJoin(updates).subscribe({
            next: () => {
                this.isBulkEditSaving = false;
                this.toast.success(`${modifiedClauses.length} clause(s) updated successfully`);
                this.showBulkEditDialog = false;
                this.skip = 0;
                this.getClauses();
                this.getClauseBundles();
                this.onCancelBulkEdit();
            },
            error: () => {
                this.isBulkEditSaving = false;
                this.toast.error('Failed to update clauses');
                this.clearSelections();
            }
        });
    }

    saveIncompleteClauses() {
        this.confirmationService.confirm({
            message: 'You have unsaved changes in some clauses. Save only the modified clauses?',
            header: 'Save incomplete?',
            accept: () => this.onSaveBulkEdit(false),
            reject: () => { }
        });
    }

    resetBulkEditDialog() {
        this.totalEditingCount = 0;
        this.isBulkEditPreviousDisabled = true;
        this.isBulkEditNextDisabled = true;
        this.isBulkEditSaveDisabled = true;
        this.savedClauses.clear();
    }

    bulkApproveClauses() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause');
            return;
        }
        this.showBulkApproveConfirmDialog = true;
    }

    bulkRejectClauses() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause');
            return;
        }
        this.showBulkRejectConfirmDialog = true;
    }

    confirmBulkApprove() {
        this.showBulkApproveConfirmDialog = false;
        this.clauseLibraryService.bulkUpdateClauses(this.selectedClauseIds.map(id => ({ _id: id, status: CLAUSE_STATUS.APPROVED }))).subscribe({
            next: (res) => {
                if (res && res.success) {
                    this.toast.success(`${this.selectedClauseIds.length} clause(s) approved`);
                    this.clearSelections();
                    this.skip = 0;
                    this.getClauses();
                }
            },
            error: () => this.toast.error('Failed to approve clauses')
        });
    }

    cancelBulkApproveConfirm() {
        this.showBulkApproveConfirmDialog = false;
    }

    confirmBulkReject() {
        this.showBulkRejectConfirmDialog = false;
        this.clauseLibraryService.bulkUpdateClauses(this.selectedClauseIds.map(id => ({ _id: id, status: CLAUSE_STATUS.REJECTED }))).subscribe({
            next: (res) => {
                if (res && res.success) {
                    this.toast.success(`${this.selectedClauseIds.length} clause(s) rejected`);
                    this.clearSelections();
                    this.skip = 0;
                    this.getClauses();
                }
            },
            error: () => this.toast.error('Failed to reject clauses')
        });
    }

    cancelBulkRejectConfirm() {
        this.showBulkRejectConfirmDialog = false;
    }

    viewClauseDetails(clause: IClause) {
        this.viewClause = clause;
        this.showViewDialog = true;
    }

    saveClause() {
        if (!this.currentClause.name) {
            this.toast.error('Please fill in required fields');
            return;
        }
        this.saveClauseLoading = true;
        if (this.isEditMode && this.currentClause._id) {
            this.clauseLibraryService.updateClause(this.currentClause._id, {
                name: this.currentClause.name,
                text: this.currentClause.text,
                status: this.currentClause.status,
                clauseBundles: this.currentClauseBundleIds
            }).subscribe({
                next: (res) => {
                    this.saveClauseLoading = false;
                    if (res.success) {
                        this.toast.success('Clause updated');
                        this.showAddEditDialog = false;
                        this.isClauseTextExpanded = false;
                        this.skip = 0;
                        this.getClauses();
                        this.getClauseBundles();
                        this.clearSelections();
                    }
                },
                error: () => {
                    this.saveClauseLoading = false;
                    this.toast.error('Failed to update clause');
                }
            });
        }
    }

    deleteSingleClause(clause: IClause) {
        this.selectedClauseIds = [clause._id];
        this.itemsToDeleteCount = 1;
        this.showDeleteConfirmDialog = true;
    }

    bulkDeleteClauses() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause');
            return;
        }
        this.itemsToDeleteCount = this.selectedClauseIds.length;
        this.showDeleteConfirmDialog = true;
    }

    confirmDelete() {
        this.showDeleteConfirmDialog = false;
        this.clauseLibraryService.bulkDeleteClauses(this.selectedClauseIds).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toast.success(`${this.itemsToDeleteCount} clauses deleted`);
                    this.clearSelections();
                    this.skip = 0;
                    this.getClauses();
                    this.getClauseBundles();
                }
            },
            error: () => this.toast.error('Failed to delete clauses')
        });
    }

    cancelDelete() {
        this.showDeleteConfirmDialog = false;
        this.itemsToDeleteCount = 0;
    }

    bulkDownloadClauses() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause');
            return;
        }
        this.clauseLibraryService.bulkDownloadClauses(this.selectedClauseIds).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    let blob: Blob;
                    let filename: string;

                    blob = new Blob([new Uint8Array(res.data.data)], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
                    });
                    filename = 'clauses.xlsx';
                    saveAs(blob, filename);
                    this.toast.success(`${this.selectedClauseIds.length} clauses downloaded`);
                } else {
                    this.toast.error('Failed to download');
                }
            },
            error: () => this.toast.error('Failed to download')
        });
    }

    openBulkLinkBundleDialog() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause');
            return;
        }
        this.bulkLinkSearchValue = '';
        this.selectedBundleIdsForBulkLink = [];
        this.showBulkLinkBundleDialog = true;
    }

    get filteredClauseBundles(): IClauseBundle[] {
        if (!this.bulkLinkSearchValue.trim()) return this.clauseBundles;
        const q = this.bulkLinkSearchValue.toLowerCase();
        return this.clauseBundles.filter(b => b.name.toLowerCase().includes(q));
    }

    toggleBundleSelection(bundleId: string) {
        const i = this.selectedBundleIdsForBulkLink.indexOf(bundleId);
        if (i > -1) this.selectedBundleIdsForBulkLink.splice(i, 1);
        else this.selectedBundleIdsForBulkLink.push(bundleId);
    }

    isBundleSelected(bundleId: string) {
        return this.selectedBundleIdsForBulkLink.includes(bundleId);
    }

    submitBulkLinkToBundles() {
        if (this.selectedBundleIdsForBulkLink.length === 0) {
            this.toast.error('Please select at least one clause bundle');
            return;
        }
        this.bulkLinkingToBundles = true;
        this.clauseLibraryService.bulkLinkClausesToBundles(this.selectedClauseIds, this.selectedBundleIdsForBulkLink).subscribe({
            next: (res) => {
                this.bulkLinkingToBundles = false;
                if (res.success) {
                    this.toast.success(`${this.selectedClauseIds.length} clauses linked to bundle(s)`);
                    this.showBulkLinkBundleDialog = false;
                    this.clearSelections();
                    this.skip = 0;
                    this.getClauses();
                    this.getClauseBundles();
                }
            },
            error: () => {
                this.bulkLinkingToBundles = false;
                this.toast.error('Failed to link');
            }
        });
    }
}
